import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import './cardStage.css';

export type CardVisual = {
  id: string;
  name: string;
  sport: string;
  position: string;
  rating: number;
  rarity: string;
  edition: string;
  accent: string;
  image: string;
  /** Position in the supplied 3-column / 2-row athlete image atlas. */
  imagePosition?: string;
};

type StageController = {
  flip: () => void;
  reset: () => void;
  rotate: (direction: number) => void;
  zoom: (amount: number) => void;
};

const BRAND = '#FB016F';
const DISPLAY = '"Big Shoulders Display", Impact, "Arial Narrow", sans-serif';
const BODY = 'Archivo, Arial, sans-serif';
const CW = 1050;
const CH = 1550;

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, radius);
}

function label(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, size = 19, color = '#fff', spacing = 3) {
  ctx.font = `600 ${size}px ${BODY}`;
  ctx.fillStyle = color;
  let next = x;
  for (const char of text.toUpperCase()) {
    ctx.fillText(char, next, y);
    next += ctx.measureText(char).width + spacing;
  }
}

function fittedText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, size: number, color: string) {
  let actual = size;
  ctx.font = `900 ${actual}px ${DISPLAY}`;
  while (ctx.measureText(text).width > maxWidth && actual > 40) {
    actual -= 2;
    ctx.font = `900 ${actual}px ${DISPLAY}`;
  }
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
}

function athleteCrop(ctx: CanvasRenderingContext2D, card: CardVisual, image: HTMLImageElement) {
  let sx = 0;
  let sy = 0;
  let sw = image.naturalWidth;
  let sh = image.naturalHeight;
  if (card.imagePosition) {
    const values = card.imagePosition.match(/[\d.]+/g)?.map(Number) || [50, 0];
    sw /= 3;
    sh /= 2;
    sx = Math.max(0, Math.min(1, values[0] / 100)) * (image.naturalWidth - sw);
    sy = Math.max(0, Math.min(1, (values[1] || 0) / 100)) * (image.naturalHeight - sh);
  }
  const dx = 26;
  const dy = 26;
  const dw = CW - 52;
  const dh = 1290;
  const scale = Math.max(dw / sw, dh / sh);
  const croppedWidth = dw / scale;
  const croppedHeight = dh / scale;
  ctx.drawImage(image, sx + (sw - croppedWidth) / 2, sy + (sh - croppedHeight) * .34, croppedWidth, croppedHeight, dx, dy, dw, dh);
}

function drawFront(canvas: HTMLCanvasElement, card: CardVisual, portrait?: HTMLImageElement, logo?: HTMLImageElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, CW, CH);
  ctx.save();
  roundRect(ctx, 4, 4, CW - 8, CH - 8, 38);
  ctx.clip();
  ctx.fillStyle = '#09090b';
  ctx.fillRect(0, 0, CW, CH);
  if (portrait) athleteCrop(ctx, card, portrait);
  else {
    const gradient = ctx.createLinearGradient(0, 0, CW, CH);
    gradient.addColorStop(0, '#191419');
    gradient.addColorStop(.7, card.accent);
    gradient.addColorStop(1, '#060607');
    ctx.fillStyle = gradient;
    ctx.fillRect(25, 25, CW - 50, CH - 50);
  }
  const shade = ctx.createLinearGradient(0, 0, 0, CH);
  shade.addColorStop(0, 'rgba(0,0,0,.55)');
  shade.addColorStop(.25, 'rgba(0,0,0,0)');
  shade.addColorStop(.49, 'rgba(0,0,0,.02)');
  shade.addColorStop(.66, 'rgba(5,5,7,.50)');
  shade.addColorStop(.81, 'rgba(5,5,7,.98)');
  shade.addColorStop(1, '#050507');
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, CW, CH);
  const edge = ctx.createLinearGradient(0, 0, CW, CH);
  edge.addColorStop(0, '#eae1e7');
  edge.addColorStop(.18, card.accent);
  edge.addColorStop(.43, '#32242e');
  edge.addColorStop(.70, '#fed3e5');
  edge.addColorStop(1, card.accent);
  ctx.strokeStyle = edge;
  ctx.lineWidth = 10;
  roundRect(ctx, 12, 12, CW - 24, CH - 24, 32);
  ctx.stroke();
  ctx.strokeStyle = '#ffffff30';
  ctx.lineWidth = 1.5;
  roundRect(ctx, 27, 27, CW - 54, CH - 54, 24);
  ctx.stroke();

  // The narrow translucent facets read as foil when the object moves under light.
  ctx.save();
  ctx.globalAlpha = .065;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.moveTo(CW * .76, 0);
  ctx.lineTo(CW, 0);
  ctx.lineTo(CW * .34, CH);
  ctx.lineTo(CW * .16, CH);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  if (logo) {
    const ratio = logo.naturalWidth / logo.naturalHeight || 1.5;
    ctx.drawImage(logo, 67, 71, 127, 127 / ratio);
  } else label(ctx, 'ANAVRIN', 70, 123, 29, BRAND, 4);
  label(ctx, card.sport, 70, 200, 21, '#f5f3f4', 3);
  ctx.textAlign = 'right';
  ctx.font = `900 154px ${DISPLAY}`;
  ctx.fillStyle = '#fbf9f4';
  ctx.fillText(String(card.rating), 972, 190);
  ctx.font = `600 17px ${BODY}`;
  ctx.fillStyle = '#dcd5d8';
  ctx.fillText('PLAYER RATING', 966, 220);
  ctx.textAlign = 'left';

  ctx.fillStyle = card.accent;
  ctx.fillRect(66, 1051, 6, 34);
  label(ctx, `${card.rarity} / ${card.edition}`, 91, 1074, 19, '#f9eaf0', 2.2);
  const parts = card.name.trim().split(/\s+/);
  const last = parts.pop() || card.name;
  const first = parts.join(' ');
  fittedText(ctx, first.toUpperCase(), 67, 1160, 918, 85, '#f6f4f4');
  fittedText(ctx, last.toUpperCase(), 65, 1300, 918, 150, '#fff');
  label(ctx, card.position, 70, 1356, 20, '#b7b0b5', 3);
  ctx.strokeStyle = '#ffffff35';
  ctx.beginPath();
  ctx.moveTo(68, 1400);
  ctx.lineTo(980, 1400);
  ctx.stroke();
  label(ctx, 'ANAVRIN ORIGINAL', 70, 1468, 19, '#ede4e9', 3);
  ctx.textAlign = 'right';
  ctx.font = `500 18px ${BODY}`;
  ctx.fillStyle = '#aba2a9';
  ctx.fillText('COLLECT THE NEXT', 970, 1468);
  ctx.textAlign = 'left';
  ctx.restore();
}

function drawBack(canvas: HTMLCanvasElement, card: CardVisual, logo?: HTMLImageElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, CW, CH);
  ctx.save();
  roundRect(ctx, 4, 4, CW - 8, CH - 8, 38);
  ctx.clip();
  const gradient = ctx.createRadialGradient(CW * .55, CH * .40, 10, CW * .5, CH * .5, 1000);
  gradient.addColorStop(0, '#30101f');
  gradient.addColorStop(.65, '#10090e');
  gradient.addColorStop(1, '#070709');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, CW, CH);
  ctx.strokeStyle = `${card.accent}38`;
  ctx.lineWidth = 1.5;
  for (let i = -CH; i < CW + CH; i += 70) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + CH, CH);
    ctx.stroke();
  }
  ctx.strokeStyle = card.accent;
  ctx.lineWidth = 7;
  roundRect(ctx, 15, 15, CW - 30, CH - 30, 30);
  ctx.stroke();
  ctx.strokeStyle = '#ffffff30';
  ctx.lineWidth = 1;
  roundRect(ctx, 35, 35, CW - 70, CH - 70, 23);
  ctx.stroke();
  label(ctx, 'THE NEXT IS YOURS.', 70, 112, 23, '#dcd2d9', 5);
  if (logo) {
    const ratio = logo.naturalWidth / logo.naturalHeight || 1.5;
    const width = 580;
    ctx.drawImage(logo, (CW - width) / 2, 445, width, width / ratio);
  }
  ctx.textAlign = 'center';
  ctx.fillStyle = '#fff';
  ctx.font = `900 102px ${DISPLAY}`;
  ctx.fillText('ANAVRIN', CW / 2, 965);
  ctx.font = `500 20px ${BODY}`;
  ctx.fillStyle = '#c8bfc5';
  ctx.fillText('EVERY ATHLETE HAS A STORY.', CW / 2, 1026);
  ctx.fillText('BE PART OF THE NEXT CHAPTER.', CW / 2, 1064);
  ctx.textAlign = 'left';
  ctx.strokeStyle = '#ffffff2b';
  ctx.beginPath();
  ctx.moveTo(70, 1200);
  ctx.lineTo(980, 1200);
  ctx.stroke();
  label(ctx, card.name, 70, 1274, 23, '#fff', 2);
  label(ctx, `${card.sport} · ${card.position}`, 70, 1320, 18, '#bcb1b8', 1.4);
  label(ctx, `${card.rarity} / ${card.edition}`, 70, 1420, 20, card.accent, 2);
  label(ctx, 'DESIGN PREVIEW', 70, 1470, 15, '#857680', 3);
  ctx.restore();
}

function loadImage(url: string): Promise<HTMLImageElement | undefined> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(undefined);
    image.src = url;
  });
}

function roundedCardShape(width: number, height: number, radius: number) {
  const shape = new THREE.Shape();
  const x = -width / 2;
  const y = -height / 2;
  shape.moveTo(x + radius, y);
  shape.lineTo(x + width - radius, y);
  shape.quadraticCurveTo(x + width, y, x + width, y + radius);
  shape.lineTo(x + width, y + height - radius);
  shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  shape.lineTo(x + radius, y + height);
  shape.quadraticCurveTo(x, y + height, x, y + height - radius);
  shape.lineTo(x, y + radius);
  shape.quadraticCurveTo(x, y, x + radius, y);
  return shape;
}

function StageIcon({ name }: { name: 'rotate' | 'flip' | 'reset' | 'plus' | 'minus' }) {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {name === 'rotate' && <><path d="m4 8 4-4 4 4"/><path d="M8 4v12a4 4 0 0 0 8 0V8"/><path d="m12 12 4-4 4 4"/></>}
    {name === 'flip' && <><rect x="5" y="4" width="14" height="16" rx="2"/><path d="M12 4v16m-3-8h6m-2-2 2 2-2 2"/></>}
    {name === 'reset' && <><path d="M3 10a9 9 0 1 1 2.5 8M3 4v6h6"/></>}
    {name === 'plus' && <path d="M5 12h14M12 5v14"/>}
    {name === 'minus' && <path d="M5 12h14"/>}
  </svg>;
}

export function CardStage({ card, interactive = true, compact = false, className = '' }: {
  card: CardVisual;
  interactive?: boolean;
  compact?: boolean;
  className?: string;
}) {
  const mountRef = useRef<HTMLDivElement>(null);
  const controller = useRef<StageController | null>(null);
  const [fallback, setFallback] = useState(false);
  const [ready, setReady] = useState(false);
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    const host = mountRef.current;
    if (!host) return;
    setFallback(false);
    setReady(false);
    setFlipped(false);
    let alive = true;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'low-power' });
    } catch {
      setFallback(true);
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.18;
    const canvas = renderer.domElement;
    canvas.className = 'card-stage__canvas';
    canvas.setAttribute('aria-label', `${card.name} interactive player card. Drag to rotate, double click to flip. Use arrow keys to rotate, plus and minus to zoom.`);
    canvas.setAttribute('role', 'img');
    canvas.tabIndex = interactive ? 0 : -1;
    host.appendChild(canvas);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(33, 1, .1, 100);
    camera.position.set(0, .02, compact ? 9.0 : 9.1);
    const cardGroup = new THREE.Group();
    scene.add(cardGroup);
    const frontCanvas = document.createElement('canvas');
    frontCanvas.width = CW;
    frontCanvas.height = CH;
    const backCanvas = document.createElement('canvas');
    backCanvas.width = CW;
    backCanvas.height = CH;
    drawFront(frontCanvas, card);
    drawBack(backCanvas, card);
    const frontTexture = new THREE.CanvasTexture(frontCanvas);
    const backTexture = new THREE.CanvasTexture(backCanvas);
    for (const texture of [frontTexture, backTexture]) {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
    }
    const faceGeometry = new THREE.PlaneGeometry(3.20, 4.724);
    const frontMaterial = new THREE.MeshPhysicalMaterial({
      map: frontTexture, roughness: .48, metalness: .08,
      clearcoat: .95, clearcoatRoughness: .19, transparent: true, alphaTest: .05,
    });
    const backMaterial = new THREE.MeshPhysicalMaterial({
      map: backTexture, roughness: .4, metalness: .12,
      clearcoat: .9, clearcoatRoughness: .2, transparent: true, alphaTest: .05,
    });
    const front = new THREE.Mesh(faceGeometry, frontMaterial);
    front.position.z = .076;
    cardGroup.add(front);
    const back = new THREE.Mesh(faceGeometry, backMaterial);
    back.rotation.y = Math.PI;
    back.position.z = -.035;
    cardGroup.add(back);
    const edgeGeometry = new THREE.ExtrudeGeometry(roundedCardShape(3.19, 4.71, .105), {
      depth: .042, bevelEnabled: true, bevelSize: .013, bevelThickness: .018, bevelSegments: 3, steps: 1, curveSegments: 12,
    });
    const edgeMaterial = new THREE.MeshStandardMaterial({ color: '#d4c4cd', metalness: .93, roughness: .25 });
    const body = new THREE.Mesh(edgeGeometry, edgeMaterial);
    cardGroup.add(body);

    const ambient = new THREE.AmbientLight(0xffffff, 2.25);
    const key = new THREE.DirectionalLight(0xffffff, 2.9);
    key.position.set(-3, 4, 6);
    const rim = new THREE.DirectionalLight(card.accent, 2.0);
    rim.position.set(4, -1, 2);
    const reverse = new THREE.DirectionalLight(0xffffff, 2.4);
    reverse.position.set(1, 2, -6);
    scene.add(ambient, key, rim, reverse);

    let portrait: HTMLImageElement | undefined;
    let logo: HTMLImageElement | undefined;
    const fontsReady = Promise.all([
      document.fonts.load('900 48px "Big Shoulders Display"'),
      document.fonts.load('600 20px Archivo'),
    ]).then(() => document.fonts.ready);
    Promise.all([loadImage(card.image), loadImage('/assets/anavrin-logo.svg'), fontsReady]).then((result) => {
      if (!alive) return;
      portrait = result[0];
      logo = result[1];
      drawFront(frontCanvas, card, portrait, logo);
      drawBack(backCanvas, card, logo);
      frontTexture.needsUpdate = true;
      backTexture.needsUpdate = true;
      setReady(true);
      renderer.render(scene, camera);
    });

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let targetX = -.04;
    let targetY = -.30;
    let targetZ = -.065;
    let targetDistance = compact ? 9 : 9.1;
    let flipState = false;
    let pointerStartX = 0;
    let pointerStartY = 0;
    let originalX = 0;
    let originalY = 0;
    let dragging = false;
    let hovering = false;
    let inView = true;
    let raf = 0;
    let lastFrame = 0;
    let elapsed = 0;
    cardGroup.rotation.set(targetX, targetY, targetZ);

    const resize = () => {
      if (!alive) return;
      const width = Math.max(1, host.clientWidth);
      const height = Math.max(1, host.clientHeight);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      // A narrow phone frame needs a little more camera distance to preserve edges.
      camera.fov = camera.aspect < .72 ? 37 : 33;
      camera.updateProjectionMatrix();
      renderer.render(scene, camera);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    resize();

    const renderFrame = (time: number) => {
      raf = 0;
      if (!alive || !inView || document.hidden) return;
      const delta = Math.min((time - (lastFrame || time)) / 1000, .05);
      elapsed += delta;
      lastFrame = time;
      const blend = reducedMotion.matches ? 1 : 1 - Math.pow(.0009, delta);
      cardGroup.rotation.x += (targetX - cardGroup.rotation.x) * blend;
      cardGroup.rotation.y += (targetY - cardGroup.rotation.y) * blend;
      cardGroup.rotation.z += (targetZ - cardGroup.rotation.z) * blend;
      cardGroup.position.y = reducedMotion.matches || dragging ? 0 : Math.sin(elapsed * .8) * .055;
      camera.position.z += (targetDistance - camera.position.z) * blend;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(renderFrame);
    };
    const resume = () => {
      lastFrame = 0;
      if (!raf && inView && !document.hidden) raf = requestAnimationFrame(renderFrame);
    };
    const visibilityChange = () => {
      if (document.hidden) { cancelAnimationFrame(raf); raf = 0; }
      else resume();
    };
    const intersection = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      if (inView) resume();
      else { cancelAnimationFrame(raf); raf = 0; }
    }, { rootMargin: '100px' });
    intersection.observe(host);
    document.addEventListener('visibilitychange', visibilityChange);

    const flip = () => {
      flipState = !flipState;
      targetY = flipState ? Math.PI - .20 : -.30;
      targetX = -.04;
      targetZ = flipState ? .04 : -.065;
      setFlipped(flipState);
    };
    const zoom = (amount: number) => { targetDistance = THREE.MathUtils.clamp(targetDistance + amount, 7.2, 11.5); };
    const reset = () => {
      flipState = false;
      targetX = -.04;
      targetY = -.30;
      targetZ = -.065;
      targetDistance = compact ? 9 : 9.1;
      setFlipped(false);
    };
    const rotate = (direction: number) => { targetY += direction * Math.PI / 6; };
    controller.current = { flip, reset, zoom, rotate };

    const pointerDown = (event: PointerEvent) => {
      if (!interactive || event.button !== 0) return;
      dragging = true;
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      originalX = targetX;
      originalY = targetY;
      canvas.setPointerCapture(event.pointerId);
      canvas.classList.add('is-dragging');
      canvas.focus({ preventScroll: true });
    };
    const pointerMove = (event: PointerEvent) => {
      if (!interactive) return;
      if (dragging) {
        targetY = originalY + (event.clientX - pointerStartX) * .010;
        targetX = THREE.MathUtils.clamp(originalX + (event.clientY - pointerStartY) * .005, -.65, .65);
      } else if (hovering && event.pointerType !== 'touch' && !reducedMotion.matches) {
        const rect = canvas.getBoundingClientRect();
        targetY = (flipState ? Math.PI - .20 : -.30) + ((event.clientX - rect.left) / rect.width - .5) * .35;
        targetX = -.04 + ((event.clientY - rect.top) / rect.height - .5) * .14;
      }
    };
    const pointerUp = () => { dragging = false; canvas.classList.remove('is-dragging'); };
    const pointerEnter = () => { hovering = true; };
    const pointerLeave = () => {
      hovering = false;
      if (!dragging) { targetX = -.04; targetY = flipState ? Math.PI - .20 : -.30; }
    };
    const doubleClick = () => { if (interactive) flip(); };
    const keyDown = (event: KeyboardEvent) => {
      if (!interactive) return;
      if (event.key === 'ArrowLeft') rotate(-1);
      else if (event.key === 'ArrowRight') rotate(1);
      else if (event.key === '+' || event.key === '=') zoom(-.5);
      else if (event.key === '-') zoom(.5);
      else if (event.key === ' ' || event.key.toLowerCase() === 'f') flip();
      else if (event.key === 'Home' || event.key.toLowerCase() === 'r') reset();
      else return;
      event.preventDefault();
    };
    const wheel = (event: WheelEvent) => {
      if (!interactive || document.activeElement !== canvas || !(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      zoom(event.deltaY * .008);
    };
    const contextLost = (event: Event) => {
      event.preventDefault();
      cancelAnimationFrame(raf);
      raf = 0;
      inView = false;
      setFallback(true);
    };
    canvas.addEventListener('pointerdown', pointerDown);
    canvas.addEventListener('pointermove', pointerMove);
    canvas.addEventListener('pointerup', pointerUp);
    canvas.addEventListener('pointercancel', pointerUp);
    canvas.addEventListener('pointerenter', pointerEnter);
    canvas.addEventListener('pointerleave', pointerLeave);
    canvas.addEventListener('dblclick', doubleClick);
    canvas.addEventListener('keydown', keyDown);
    canvas.addEventListener('wheel', wheel, { passive: false });
    canvas.addEventListener('webglcontextlost', contextLost);
    resume();

    return () => {
      alive = false;
      controller.current = null;
      cancelAnimationFrame(raf);
      observer.disconnect();
      intersection.disconnect();
      document.removeEventListener('visibilitychange', visibilityChange);
      canvas.removeEventListener('pointerdown', pointerDown);
      canvas.removeEventListener('pointermove', pointerMove);
      canvas.removeEventListener('pointerup', pointerUp);
      canvas.removeEventListener('pointercancel', pointerUp);
      canvas.removeEventListener('pointerenter', pointerEnter);
      canvas.removeEventListener('pointerleave', pointerLeave);
      canvas.removeEventListener('dblclick', doubleClick);
      canvas.removeEventListener('keydown', keyDown);
      canvas.removeEventListener('wheel', wheel);
      canvas.removeEventListener('webglcontextlost', contextLost);
      frontTexture.dispose();
      backTexture.dispose();
      faceGeometry.dispose();
      edgeGeometry.dispose();
      frontMaterial.dispose();
      backMaterial.dispose();
      edgeMaterial.dispose();
      scene.clear();
      renderer.dispose();
      renderer.forceContextLoss();
      canvas.remove();
    };
  }, [card.id, card.name, card.sport, card.position, card.rating, card.rarity, card.edition, card.accent, card.image, card.imagePosition, interactive, compact]);

  return <div className={`card-stage ${compact ? 'card-stage--compact' : ''} ${ready ? 'card-stage--ready' : ''} ${className}`} style={{ '--card-accent': card.accent } as React.CSSProperties}>
    <div className="card-stage__halo" aria-hidden="true" />
    <div ref={mountRef} className={`card-stage__viewport ${fallback ? 'card-stage__viewport--hidden' : ''}`} />
    {fallback && <div className="card-stage__fallback">
      <div className="card-stage__fallback-photo" style={{ backgroundImage: `url("${card.image}")`, backgroundPosition: card.imagePosition || 'center', backgroundSize: card.imagePosition ? '300% 200%' : 'cover' }} />
      <div className="card-stage__fallback-top"><img src="/assets/anavrin-logo.svg" alt="ANAVRIN"/><span>{card.rating}</span></div>
      <div className="card-stage__fallback-info"><small>{card.rarity} · {card.edition}</small><strong>{card.name}</strong><span>{card.sport} / {card.position}</span></div>
    </div>}
    {interactive && !fallback && <div className="card-stage__interaction">
      <span className="card-stage__hint">{flipped ? 'EVERY CARD. A NEW STORY.' : 'GO ON, GIVE IT A SPIN.'}</span>
      <div className="card-stage__controls" aria-label="3D player card controls">
        <button type="button" onClick={() => controller.current?.rotate(-1)} aria-label="Rotate card left" title="Rotate left"><StageIcon name="rotate"/></button>
        <button type="button" onClick={() => controller.current?.flip()} aria-label="Flip player card" title="Flip card"><StageIcon name="flip"/><span>Flip</span></button>
        <span className="card-stage__divider"/>
        <button type="button" onClick={() => controller.current?.zoom(-.6)} aria-label="Zoom into player card" title="Zoom in"><StageIcon name="plus"/></button>
        <button type="button" onClick={() => controller.current?.zoom(.6)} aria-label="Zoom out of player card" title="Zoom out"><StageIcon name="minus"/></button>
        <button type="button" onClick={() => controller.current?.reset()} aria-label="Reset player card view" title="Reset view"><StageIcon name="reset"/></button>
      </div>
    </div>}
  </div>;
}

export default CardStage;
